import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { api, formatPrice } from "@/lib/api";

const FREE_SHIPPING_THRESHOLD = 150;

export const CartDrawer = () => {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal, discount, total, promo, setPromo } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.product.id, size: i.size, quantity: i.quantity })),
        origin_url: window.location.origin,
        promo_code: promo || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Impossible de lancer le paiement", {
        description: e.response?.data?.detail || "Réessayez dans un instant.",
      });
      setLoading(false);
    }
  };

  const progress = Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-md"
            data-testid="cart-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 260 }}
            data-testid="cart-drawer"
            className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-md bg-coal border-l border-white/10 flex flex-col"
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="font-syne font-extrabold uppercase tracking-tight text-lg">Mon Panier</h2>
              <button data-testid="cart-close-button" onClick={() => setIsOpen(false)} className="p-2 hover:text-blood transition-colors" aria-label="Fermer le panier">
                <X size={20} />
              </button>
            </div>

            <div className="px-6 pt-5">
              <div className="flex justify-between font-mono text-[10px] uppercase tracking-[0.15em] text-ash mb-2">
                <span>{subtotal >= FREE_SHIPPING_THRESHOLD ? "Livraison offerte" : "Livraison offerte dès 150 €"}</span>
                <span>{formatPrice(subtotal)} / 150 €</span>
              </div>
              <div className="h-1 bg-white/10">
                <div className="h-full bg-volt transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {items.length === 0 && (
                <p data-testid="cart-empty-message" className="text-ash text-sm text-center pt-16 font-mono uppercase tracking-[0.2em]">
                  Panier vide
                </p>
              )}
              {items.map((i) => (
                <div key={`${i.product.id}-${i.size}`} data-testid={`cart-item-${i.product.id}-${i.size}`} className="flex gap-4 border border-white/10 p-3">
                  <img src={i.product.image} alt={i.product.name} className="w-20 h-24 object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <h3 className="font-syne font-bold uppercase text-xs leading-snug">{i.product.name}</h3>
                      <button data-testid={`cart-remove-${i.product.id}-${i.size}`} onClick={() => removeItem(i.product.id, i.size)} className="text-ash hover:text-blood transition-colors" aria-label="Retirer">
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <p className="font-mono text-[10px] text-ash mt-1 uppercase tracking-[0.15em]">Taille {i.size}</p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-white/15">
                        <button data-testid={`cart-qty-minus-${i.product.id}-${i.size}`} onClick={() => updateQty(i.product.id, i.size, -1)} className="p-1.5 hover:text-blood" aria-label="Diminuer">
                          <Minus size={13} />
                        </button>
                        <span className="font-mono text-xs w-8 text-center">{i.quantity}</span>
                        <button data-testid={`cart-qty-plus-${i.product.id}-${i.size}`} onClick={() => updateQty(i.product.id, i.size, 1)} className="p-1.5 hover:text-volt" aria-label="Augmenter">
                          <Plus size={13} />
                        </button>
                      </div>
                      <span className="font-mono text-sm">{formatPrice(i.product.price * i.quantity)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {items.length > 0 && (
              <div className="border-t border-white/10 p-6 space-y-4">
                <div className="flex gap-2">
                  <input
                    data-testid="cart-promo-input"
                    value={promo}
                    onChange={(e) => setPromo(e.target.value)}
                    placeholder="CODE PROMO (STREET10)"
                    className="flex-1 bg-ink border border-white/15 px-3 py-2.5 font-mono text-xs uppercase tracking-[0.15em] placeholder:text-ash focus:outline-none focus:border-volt"
                  />
                </div>
                <div className="space-y-1.5 font-mono text-xs">
                  <div className="flex justify-between text-smoke">
                    <span>Sous-total</span>
                    <span data-testid="cart-subtotal">{formatPrice(subtotal)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-volt">
                      <span>Promo STREET10 (-10%)</span>
                      <span data-testid="cart-discount">-{formatPrice(discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-base pt-2 border-t border-white/10">
                    <span className="uppercase tracking-[0.15em]">Total</span>
                    <span data-testid="cart-total" className="text-volt">{formatPrice(total)}</span>
                  </div>
                </div>
                <button
                  data-testid="cart-checkout-button"
                  onClick={checkout}
                  disabled={loading}
                  className="w-full bg-blood text-white font-mono text-xs uppercase tracking-[0.2em] py-4 flex items-center justify-center gap-3 hover:bg-white hover:text-ink transition-colors disabled:opacity-50"
                >
                  {loading ? "Redirection vers Stripe…" : "Commander"}
                  {!loading && <ArrowRight size={15} />}
                </button>
                <p className="text-center font-mono text-[10px] text-ash uppercase tracking-[0.15em]">
                  Paiement sécurisé via Stripe
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
